import { AlertTriangle, Plus } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import type { Ingredient } from "@/hooks/useIngredients";
import type { StockProduct } from "@/hooks/useProductStock";

interface LowStockItem {
  id: string;
  name: string;
  current_stock: number;
  minimum_stock: number;
  unit: string;
  type: "ingredient" | "product";
}

interface LowStockAlertProps {
  ingredients?: Ingredient[];
  products?: StockProduct[];
  onAddIngredientStock?: (ingredient: Ingredient) => void;
  onAddProductStock?: (product: StockProduct) => void;
}

export function LowStockAlert({
  ingredients = [],
  products = [],
  onAddIngredientStock,
  onAddProductStock,
}: LowStockAlertProps) {
  // Combine both into a unified list
  const lowStockItems: LowStockItem[] = [
    ...ingredients.map((ing) => ({
      id: ing.id,
      name: ing.name,
      current_stock: ing.current_stock,
      minimum_stock: ing.minimum_stock,
      unit: ing.unit,
      type: "ingredient" as const,
    })),
    ...products.map((prod) => ({
      id: prod.id,
      name: prod.name,
      current_stock: prod.current_stock,
      minimum_stock: prod.minimum_stock,
      unit: "un",
      type: "product" as const,
    })),
  ];

  if (lowStockItems.length === 0) return null;

  const handleAddStock = (item: LowStockItem) => {
    if (item.type === "ingredient") {
      const ingredient = ingredients.find((i) => i.id === item.id);
      if (ingredient && onAddIngredientStock) {
        onAddIngredientStock(ingredient);
      }
    } else {
      const product = products.find((p) => p.id === item.id);
      if (product && onAddProductStock) {
        onAddProductStock(product);
      }
    }
  };

  return (
    <Alert variant="destructive" className="border-destructive/50 bg-destructive/10">
      <AlertTriangle className="h-4 w-4" />
      <AlertTitle>Atenção: Estoque Baixo</AlertTitle>
      <AlertDescription>
        <p className="mb-2">
          {lowStockItems.length} item(ns) estão com estoque abaixo do mínimo:
        </p>
        <ScrollArea className="max-h-32">
          <ul className="space-y-1">
            {lowStockItems.map((item) => (
              <li key={`${item.type}-${item.id}`} className="flex items-center justify-between gap-2 text-sm">
                <span>
                  <span className="font-medium">{item.name}</span>
                  <span className="text-muted-foreground">
                    {" — "}
                    {item.type === "ingredient" 
                      ? item.current_stock.toFixed(2) 
                      : item.current_stock
                    } {item.unit} (mín: {item.minimum_stock})
                  </span>
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-6 px-2 text-xs"
                  onClick={() => handleAddStock(item)}
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Adicionar
                </Button>
              </li>
            ))}
          </ul>
        </ScrollArea>
      </AlertDescription>
    </Alert>
  );
}
